"use client";

import type React from "react";

import { useEffect, useRef, useState, memo, useMemo, useCallback } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  Card,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  InputAdornment,
  Stack,
  Paper,
  Chip,
  TextField,
  Tooltip,
  useTheme,
  alpha,
  Collapse,
} from "@mui/material";
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  TaskAlt as TaskAltIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import WorkOrderOpenIconButtons from "../common/WorkOrderOpen/WorkOrderOpenIconButtons";
import type { ScheduledItem, ScheduleItemCalendarView } from "./lib/types";
import {
  formatHours as formatHoursUtil,
  getPriorityColorFromWorkOrder,
  getItemDisplayName,
  calculateSortKey as calculateSortKeyUtil,
  parseHoursFromHMM,
} from "./lib/utils/schedular-utils";
import {
  getCapacityIndicatorStyles,
  getCapacityStatusColor,
  dropZoneStyles,
  dropZoneAfterItemStyles,
  iconButtonSmallStyles,
  getStatusBorderStyles,
  getScheduleItemViewBorderStyles,
  iconButtonSmallStylesOpenWorkOrder,
  iconButtonSmallStylesMarkComplete,
  workOrderGroupHeaderStyles,
  workOrderGroupChildrenStyles,
  workOrderGroupTitleRowStyles,
  workOrderGroupTitleTextStyles,
  workOrderGroupMetaRowStyles,
  workOrderGroupDetailRowStyles,
  workOrderGroupDetailTextStyles,
  workOrderGroupDetailLabelStyles,
  workOrderGroupExpandToggleStyles,
  workOrderGroupExpandIndicatorStyles,
  scheduleItemActionHeaderStyles,
} from "./schedular-styles";
import { ApplicationUser } from "components/QueueBoard/models/workOrderTypes";
import { useTechnicianCalendarStore } from "../store/zustandTechnicianCalendarStore";

// Performance tracking - render counter
//let technicianCardRenderCount = 0;

interface WorkOrderRenderUnit {
  workOrderId: string;
  items: ScheduledItem[];
}

type ActiveDragPayload = {
  type: "scheduled-item" | "scheduled-item-group";
  scheduledItemId?: string;
  itemIds?: string[];
  sourceTechnicianId: number;
  sourceDay: string;
};

const sortScheduledItems = (items: ScheduledItem[]): ScheduledItem[] =>
  [...items].sort((a, b) => {
    const aKey = a.sortKey ?? a.order * 1000.0;
    const bKey = b.sortKey ?? b.order * 1000.0;
    return aKey - bKey;
  });

interface TechnicianCardProps {
  technician: ApplicationUser;
  day: string;
  date: Date;
  scheduledItems: ScheduledItem[];
  availableHours: number;
  baseCapacity: number;
  workOrders: any[];
  segmentParts: any[];
  isLocationClosed?: boolean;
  isTechnicianNotWorking?: boolean;
  onItemRemove: (itemId: string) => void | Promise<void>;
  onItemsRemove?: (itemIds: string[]) => void | Promise<void>;
  onItemComplete?: (itemId: string) => Promise<void>;
  readOnly?: boolean;
  scheduleItemView?: ScheduleItemCalendarView;
  /** Drop handler; card always supplies its own `date` prop as 2nd arg so week navigation cannot stale-close over the wrong workDate. */
  onDropZone?: (e: React.DragEvent, date: Date) => void;
  onDropAtPosition?: (
    e: React.DragEvent,
    position: number,
    date: Date,
  ) => void;
  onReorderItems?: (fromItemId: string, toSortKey: number) => void;
  onUpdateItemHours?: (itemId: string, newHours: number) => Promise<void>;
  calculateSortKey?: (items: ScheduledItem[], position: number) => number; // Helper to calculate sortKey
  onOpenWorkOrder?: (invoiceDocId: number) => void; // Callback to open work order in browser
  onOpenSmartWo?: (technicianId: number, date: string) => void;
}

function TechnicianCardComponent({
  technician,
  day,
  date,
  scheduledItems,
  availableHours,
  baseCapacity,
  workOrders,
  segmentParts,
  isLocationClosed = false,
  isTechnicianNotWorking = false,
  onItemRemove,
  onItemsRemove,
  onItemComplete,
  readOnly = false,
  scheduleItemView = "active",
  onDropZone,
  onDropAtPosition,
  onReorderItems,
  onUpdateItemHours,
  calculateSortKey,
  onOpenWorkOrder,
  onOpenSmartWo,
}: TechnicianCardProps) {
  // Performance tracking
  //technicianCardRenderCount++;
  // console.log(`[WEEK TAB] TechnicianCard render #${technicianCardRenderCount}`, {
  //   technicianId: technician.identity,
  //   technicianName: technician.userName,
  //   day,
  //   scheduledItemsCount: scheduledItems.length,
  // });

  const collapseScheduleItems = useTechnicianCalendarStore(
    (s) => s.collapseScheduleItems,
  );
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [dragOver, setDragOver] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null);
  const [hoveredDropIndex, setHoveredDropIndex] = useState<number | null>(null);
  const activeDragRef = useRef<ActiveDragPayload | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const sortedScheduledItems = useMemo(
    () => sortScheduledItems(scheduledItems),
    [scheduledItems],
  );

  const renderUnits = useMemo((): WorkOrderRenderUnit[] => {
    const units: WorkOrderRenderUnit[] = [];
    const seenWorkOrderIds = new Set<string>();

    for (const item of sortedScheduledItems) {
      if (seenWorkOrderIds.has(item.workOrderId)) {
        continue;
      }
      seenWorkOrderIds.add(item.workOrderId);

      const workOrderItems = sortedScheduledItems.filter(
        (scheduledItem) => scheduledItem.workOrderId === item.workOrderId,
      );

      units.push({
        workOrderId: item.workOrderId,
        items: workOrderItems,
      });
    }

    return units;
  }, [sortedScheduledItems]);

  const getFlatDropPosition = useCallback(
    (unitIndex: number): number => {
      let position = 0;
      for (let i = 0; i < unitIndex; i += 1) {
        position += renderUnits[i]?.items.length ?? 0;
      }
      return position;
    },
    [renderUnits],
  );

  const isSameCardDrag = useCallback(
    (drag: ActiveDragPayload) =>
      drag.sourceTechnicianId === technician.identity && drag.sourceDay === day,
    [technician.identity, day],
  );

  const clearDragState = useCallback(() => {
    activeDragRef.current = null;
    setHoveredDropIndex(null);
    setDraggedItemId(null);
    setDraggedGroupId(null);
  }, []);

  const resolveFlatDropFromEvent = (e: React.DragEvent): number | null => {
    const target = (e.target as HTMLElement | null)?.closest("[data-flat-drop]");
    if (!target) {
      return null;
    }
    const parsed = Number.parseInt(
      target.getAttribute("data-flat-drop") ?? "",
      10,
    );
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getDropZoneSx = useCallback(
    (flatIndex: number, isAfter = false): SxProps<Theme> => {
      const isActive = hoveredDropIndex === flatIndex;
      const baseStyles = isAfter ? dropZoneAfterItemStyles : dropZoneStyles;

      return {
        ...baseStyles,
        minHeight: isActive ? 28 : isAfter ? 12 : 16,
        bgcolor: isActive
          ? (theme) => alpha(theme.palette.primary.main, 0.16)
          : "transparent",
        border: isActive ? 2 : 0,
        borderStyle: "dashed",
        borderColor: "primary.main",
        borderRadius: 1,
      };
    },
    [hoveredDropIndex],
  );

  const handleDropZoneDragLeave = (e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (
      !relatedTarget ||
      !(e.currentTarget as HTMLElement).contains(relatedTarget)
    ) {
      setHoveredDropIndex(null);
    }
  };

  const adjustFlatDropPosition = useCallback(
    (
      sortedItems: ScheduledItem[],
      flatPosition: number,
      movedItemIds: string[],
    ): number => {
      if (movedItemIds.length === 0) {
        return flatPosition;
      }

      const firstMovedIndex = sortedItems.findIndex((item) =>
        movedItemIds.includes(item.id),
      );
      if (firstMovedIndex === -1 || firstMovedIndex >= flatPosition) {
        return flatPosition;
      }

      return Math.max(0, flatPosition - movedItemIds.length);
    },
    [],
  );

  const toggleGroupExpanded = useCallback((workOrderId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(workOrderId)) {
        next.delete(workOrderId);
      } else {
        next.add(workOrderId);
      }
      return next;
    });
  }, []);

  // Inline hours-editing state (per scheduled item). Only one row can be in edit mode at a time.
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editDisplayString, setEditDisplayString] = useState<string>("");
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [completingItemId, setCompletingItemId] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingItemId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingItemId]);

  const handleStartEditHours = (item: ScheduledItem) => {
    if (savingItemId) return;
    setEditingItemId(item.id);
    setEditDisplayString(formatHoursUtil(item.hours));
  };

  const handleCancelEditHours = () => {
    setEditingItemId(null);
    setEditDisplayString("");
  };

  const handleSaveEditHours = async (item: ScheduledItem) => {
    if (!onUpdateItemHours) {
      handleCancelEditHours();
      return;
    }

    const parsed = parseHoursFromHMM(editDisplayString);
    const fallback = parseFloat(editDisplayString);
    const newHours = parsed ?? (Number.isFinite(fallback) ? fallback : NaN);

    if (!Number.isFinite(newHours) || newHours <= 0) {
      enqueueSnackbar("Enter scheduled hours greater than 0.", {
        variant: "warning",
      });
      editInputRef.current?.focus();
      return;
    }

    if (newHours === item.hours) {
      handleCancelEditHours();
      return;
    }

    setEditingItemId(null);
    setSavingItemId(item.id);
    try {
      await onUpdateItemHours(item.id, newHours);
      enqueueSnackbar("Schedule has been updated.", { variant: "success" });
    } catch (error) {
      console.error("Failed to update scheduled item hours:", error);
      enqueueSnackbar("There was a problem updating the schedule.", {
        variant: "error",
      });
    } finally {
      setSavingItemId(null);
      setEditDisplayString("");
    }
  };

  const handleEditKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    item: ScheduledItem,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveEditHours(item);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEditHours();
    }
  };

  const handleCompleteItem = async (item: ScheduledItem) => {
    if (!onItemComplete || completingItemId) return;

    setCompletingItemId(item.id);
    try {
      await onItemComplete(item.id);
      enqueueSnackbar("Schedule item marked as complete.", {
        variant: "success",
      });
    } catch (error) {
      console.error("Failed to complete scheduled item:", error);
      enqueueSnackbar("Failed to mark schedule item as complete.", {
        variant: "error",
      });
    } finally {
      setCompletingItemId(null);
    }
  };

  const scheduleItemReadableTextSx = {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    whiteSpace: "normal",
    lineHeight: 1.25,
  } as const;

  const renderScheduleItemActions = (
    item: ScheduledItem,
    fullWidth = false,
  ) => (
    <Box
      sx={{
        display: "flex",
        gap: fullWidth ? 0.5 : 0.2,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        width: fullWidth ? "100%" : "auto",
        lineHeight: 0,
        "& > span": {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiIconButton-root": {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <WorkOrderOpenIconButtons
        invoiceDocId={parseInt(item.workOrderId, 10)}
        disabled={isNaN(parseInt(item.workOrderId, 10))}
        onOpenInServiceQueue={(invoiceDocId) => onOpenWorkOrder?.(invoiceDocId)}
        iconFontSize={fullWidth ? "0.8rem" : "0.75rem"}
        iconButtonSx={{
          ...iconButtonSmallStylesOpenWorkOrder,
          p: fullWidth ? 0.25 : 0.125,
          width: fullWidth ? 24 : 20,
          height: fullWidth ? 24 : 20,
          minWidth: fullWidth ? 24 : 20,
          minHeight: fullWidth ? 24 : 20,
        }}
      />
      {onItemComplete && !readOnly && (
        <Tooltip title="Mark Complete" placement="top">
          <IconButton
            size="small"
            disabled={completingItemId === item.id}
            onClick={(e) => {
              e.stopPropagation();
              handleCompleteItem(item);
            }}
            sx={{
              ...iconButtonSmallStylesMarkComplete,
              p: fullWidth ? 0.25 : 0.125,
              width: fullWidth ? 24 : 20,
              height: fullWidth ? 24 : 20,
              minWidth: fullWidth ? 24 : 20,
              minHeight: fullWidth ? 24 : 20,
            }}
          >
            <TaskAltIcon sx={{ fontSize: fullWidth ? "0.8rem" : "0.75rem" }} />
          </IconButton>
        </Tooltip>
      )}
      {!readOnly && (
        <Tooltip title="Remove" placement="top">
          <IconButton
            size="small"
            onClick={() => onItemRemove(item.id)}
            sx={{
              ...iconButtonSmallStyles,
              p: fullWidth ? 0.25 : 0.125,
              width: fullWidth ? 24 : 20,
              height: fullWidth ? 24 : 20,
              minWidth: fullWidth ? 24 : 20,
              minHeight: fullWidth ? 24 : 20,
            }}
          >
            <CloseIcon sx={{ fontSize: fullWidth ? "0.8rem" : "0.75rem" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const totalHours = scheduledItems.reduce((sum, item) => sum + item.hours, 0);
  // Use baseCapacity directly (the technician's total capacity for the day)
  const maxCapacity = baseCapacity;
  const isFull = totalHours >= maxCapacity || availableHours <= 0;

  // Check if this card's date is in the past (comparing dates only, not time)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cardDate = new Date(date);
  cardDate.setHours(0, 0, 0, 0);
  const isScheduledInPast = cardDate < today && scheduledItems.length > 0;

  // Check if card should be disabled (location closed only)
  const isDisabled = isLocationClosed;
  // Prevent drops when disabled (but allow reordering of existing items)
  const canDrop = !isDisabled && !readOnly;

  // Determine the message to show (only for location issues, not capacity)
  const getStatusMessage = () => {
    if (isLocationClosed) return "Location Closed";
    return null;
  };

  const statusMessage = getStatusMessage();

  const handleDropZoneDragOver =
    (flatIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const drag = activeDragRef.current;
      if (drag && isSameCardDrag(drag)) {
        e.dataTransfer.dropEffect = "move";
        setHoveredDropIndex(flatIndex);
        return;
      }

      if (!canDrop) {
        e.dataTransfer.dropEffect = "none";
        return;
      }

      e.dataTransfer.dropEffect = "move";
      setHoveredDropIndex(flatIndex);
    };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();

    const drag = activeDragRef.current;
    if (drag && isSameCardDrag(drag)) {
      e.dataTransfer.dropEffect = "move";
      setDragOver(true);
      return;
    }

    if (!canDrop) {
      e.dataTransfer.dropEffect = "none";
      setDragOver(false);
      return;
    }

    e.dataTransfer.dropEffect = "move";
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear dragOver if we're leaving the card itself, not just moving to a child
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const flatFromTarget = resolveFlatDropFromEvent(e);

    try {
      const dragDataStr = e.dataTransfer.getData("application/json");
      if (dragDataStr) {
        const dragData = JSON.parse(dragDataStr);
        if (
          dragData.type === "scheduled-item" ||
          dragData.type === "scheduled-item-group"
        ) {
          const isSameCard =
            dragData.sourceTechnicianId === technician.identity &&
            dragData.sourceDay === day;

          if (isSameCard) {
            e.stopPropagation();
            const flatPosition =
              flatFromTarget ?? sortedScheduledItems.length;
            handleDropAtFlatPosition(e, flatPosition);
            clearDragState();
            return;
          }

          e.stopPropagation();
          const flatPosition =
            flatFromTarget ?? sortedScheduledItems.length;
          if (onDropAtPosition) {
            onDropAtPosition(e, flatPosition, date);
          } else {
            onDropZone?.(e, date);
          }
          clearDragState();
          return;
        }
      }
    } catch {
      // Ignore parse errors, continue with normal flow
    }

    if (!canDrop) {
      clearDragState();
      return;
    }

    onDropZone?.(e, date);
    clearDragState();
  };

  const handleItemDragStart = (
    e: React.DragEvent,
    item: ScheduledItem,
    index: number,
  ) => {
    e.stopPropagation();
    setDraggedItemId(item.id);
    e.dataTransfer.effectAllowed = "move";

    // Set data for reordering within same card
    e.dataTransfer.setData("text/plain", index.toString());

    // Set data for moving between cards (as DragData)
    const dragData = {
      type: "scheduled-item" as const,
      id: item.id,
      hours: item.hours,
      workOrderId: item.workOrderId,
      scheduledItemId: item.id,
      sourceTechnicianId: technician.identity,
      sourceDay: day,
    };
    activeDragRef.current = {
      type: "scheduled-item",
      scheduledItemId: item.id,
      sourceTechnicianId: technician.identity,
      sourceDay: day,
    };
    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
  };

  const handleGroupDragStart = (
    e: React.DragEvent,
    workOrderId: string,
    items: ScheduledItem[],
  ) => {
    e.stopPropagation();
    setDraggedGroupId(workOrderId);
    e.dataTransfer.effectAllowed = "move";

    const dragData = {
      type: "scheduled-item-group" as const,
      workOrderId,
      itemIds: items.map((item) => item.id),
      sourceTechnicianId: technician.identity,
      sourceDay: day,
    };
    activeDragRef.current = {
      type: "scheduled-item-group",
      itemIds: items.map((item) => item.id),
      sourceTechnicianId: technician.identity,
      sourceDay: day,
    };
    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
  };

  const handleReorderGroup = (
    itemIds: string[],
    targetFlatPosition: number,
  ) => {
    if (!onReorderItems) return;

    const sortedItems = sortScheduledItems(scheduledItems);
    const groupItems = sortedItems.filter((item) => itemIds.includes(item.id));
    const itemsWithoutGroup = sortedItems.filter(
      (item) => !itemIds.includes(item.id),
    );

    const calcSortKey = calculateSortKey || calculateSortKeyUtil;
    const workingList = [...itemsWithoutGroup];

    groupItems.forEach((item, index) => {
      const targetSortKey = calcSortKey(workingList, targetFlatPosition + index);
      onReorderItems(item.id, targetSortKey);
      workingList.splice(targetFlatPosition + index, 0, {
        ...item,
        sortKey: targetSortKey,
      });
    });
  };

  const handleDropAtFlatPosition = (e: React.DragEvent, flatPosition: number) => {
    e.preventDefault();
    e.stopPropagation();
    setHoveredDropIndex(null);

    try {
      const dragDataStr = e.dataTransfer.getData("application/json");
      if (dragDataStr) {
        const dragData = JSON.parse(dragDataStr);

        if (
          dragData.type === "scheduled-item-group" &&
          Array.isArray(dragData.itemIds)
        ) {
          const isGroupInThisCard = dragData.itemIds.every((id: string) =>
            scheduledItems.some((item) => item.id === id),
          );

          if (isGroupInThisCard) {
            const sortedItems = sortScheduledItems(scheduledItems);
            const adjustedPosition = adjustFlatDropPosition(
              sortedItems,
              flatPosition,
              dragData.itemIds,
            );
            handleReorderGroup(dragData.itemIds, adjustedPosition);
            clearDragState();
            return;
          }

          onDropAtPosition?.(e, flatPosition, date);
          clearDragState();
          return;
        }

        if (dragData.type === "scheduled-item" && dragData.scheduledItemId) {
          const fromItemId = dragData.scheduledItemId as string;
          const isItemInThisCard = scheduledItems.some(
            (item) => item.id === fromItemId,
          );

          if (isItemInThisCard && onReorderItems) {
            const sortedItems = sortScheduledItems(scheduledItems);
            const adjustedPosition = adjustFlatDropPosition(
              sortedItems,
              flatPosition,
              [fromItemId],
            );
            const itemsWithoutMoved = sortedItems.filter(
              (item) => item.id !== fromItemId,
            );
            const calcSortKey = calculateSortKey || calculateSortKeyUtil;
            const targetSortKey = calcSortKey(
              itemsWithoutMoved,
              adjustedPosition,
            );
            onReorderItems(fromItemId, targetSortKey);
            clearDragState();
            return;
          }

          onDropAtPosition?.(e, flatPosition, date);
          clearDragState();
          return;
        }

        if (
          dragData.type === "work-order" ||
          dragData.type === "segment" ||
          dragData.type === "segment-part"
        ) {
          onDropAtPosition?.(e, flatPosition, date);
          clearDragState();
          return;
        }
      }
    } catch {
      // Ignore parse errors
    }

    clearDragState();
  };

  const handleItemDragOver = (e: React.DragEvent, flatIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setHoveredDropIndex(flatIndex);
  };

  const handleItemDrop = (e: React.DragEvent, toIndex: number) => {
    handleDropAtFlatPosition(e, toIndex);
  };

  const renderDropZone = (flatIndex: number, isAfter = false) => (
    <Box
      data-flat-drop={flatIndex}
      onDragOver={handleDropZoneDragOver(flatIndex)}
      onDragLeave={handleDropZoneDragLeave}
      onDrop={(e) => handleDropAtFlatPosition(e, flatIndex)}
      sx={getDropZoneSx(flatIndex, isAfter)}
    />
  );

  const renderGroupHeaderActions = (
    workOrderId: string,
    items: ScheduledItem[],
  ) => (
    <Box
      sx={{
        display: "flex",
        gap: 0.2,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
      }}
    >
      <WorkOrderOpenIconButtons
        invoiceDocId={parseInt(workOrderId, 10)}
        disabled={isNaN(parseInt(workOrderId, 10))}
        onOpenInServiceQueue={(invoiceDocId) => onOpenWorkOrder?.(invoiceDocId)}
        iconFontSize="0.75rem"
        iconButtonSx={{
          ...iconButtonSmallStylesOpenWorkOrder,
          p: 0.125,
          width: 20,
          height: 20,
          minWidth: 20,
          minHeight: 20,
        }}
      />
      {!readOnly && (
        <Tooltip title="Remove all segments" placement="top">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const ids = items.map((item) => item.id);
              if (onItemsRemove) {
                void onItemsRemove(ids);
              } else {
                void Promise.all(ids.map((id) => onItemRemove(id)));
              }
            }}
            sx={{
              ...iconButtonSmallStyles,
              p: 0.125,
              width: 20,
              height: 20,
              minWidth: 20,
              minHeight: 20,
            }}
          >
            <CloseIcon sx={{ fontSize: "0.75rem" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const renderGroupExpandToggle = (workOrderId: string, isExpanded: boolean) => (
    <Tooltip
      title={isExpanded ? "Collapse segments" : "Expand segments"}
      placement="top"
      arrow
    >
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleGroupExpanded(workOrderId);
        }}
        sx={workOrderGroupExpandToggleStyles}
        aria-label={isExpanded ? "Collapse segments" : "Expand segments"}
      >
        {isExpanded ? (
          <ExpandLessIcon sx={{ fontSize: "1.15rem" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: "1.15rem" }} />
        )}
      </IconButton>
    </Tooltip>
  );

  const renderGroupExpandIndicator = (isExpanded: boolean) => (
    <Box sx={workOrderGroupExpandIndicatorStyles} aria-hidden>
      {isExpanded ? (
        <ExpandLessIcon sx={{ fontSize: "1.2rem" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: "1.2rem" }} />
      )}
    </Box>
  );

  const renderWorkOrderGroup = (unit: WorkOrderRenderUnit, unitIndex: number) => {
    const { workOrderId, items } = unit;
    const isExpanded = expandedGroups.has(workOrderId);
    const isDragged = draggedGroupId === workOrderId;
    const firstItem = items[0];
    const totalGroupHours = items.reduce((sum, item) => sum + item.hours, 0);
    const priorityColor = getPriorityColorFromWorkOrder(workOrderId, workOrders);
    const displayNumber =
      firstItem.invoiceNumber || firstItem.workOrderNumber || workOrderId;
    const matchedWorkOrder = workOrders.find(
      (wo) => String(wo.id) === String(workOrderId),
    );
    const groupMyReference = (
      firstItem.woDisplayText ||
      firstItem.myReference ||
      items.find((item) => item.woDisplayText)?.woDisplayText ||
      items.find((item) => item.myReference)?.myReference ||
      matchedWorkOrder?.woDisplayText ||
      matchedWorkOrder?.myReference ||
      ""
    ).trim();
    const groupCustomerName = (
      firstItem.customerName ||
      items.find((item) => item.customerName)?.customerName ||
      matchedWorkOrder?.customerName ||
      firstItem.customerNumber ||
      items.find((item) => item.customerNumber)?.customerNumber ||
      matchedWorkOrder?.customerNumber ||
      ""
    ).trim();
    const flatStartIndex = getFlatDropPosition(unitIndex);

    return (
      <Box key={`group-${workOrderId}`}>
        <Paper
          elevation={isDragged ? 4 : 1}
          draggable={!readOnly && !isExpanded}
          data-flat-drop={flatStartIndex}
          onDragStart={(e) => handleGroupDragStart(e, workOrderId, items)}
          onDragEnd={clearDragState}
          onDragOver={handleDropZoneDragOver(flatStartIndex)}
          onDragLeave={handleDropZoneDragLeave}
          onDrop={(e) => handleDropAtFlatPosition(e, flatStartIndex)}
          sx={
            [
              workOrderGroupHeaderStyles(isExpanded, isDragged),
              scheduleItemView === "active"
                ? getStatusBorderStyles(firstItem.woStatusId, priorityColor)
                : getScheduleItemViewBorderStyles(theme, scheduleItemView),
            ] as SxProps<Theme>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.375,
              minWidth: 0,
            }}
          >
            <Box sx={workOrderGroupTitleRowStyles}>
              {renderGroupExpandToggle(workOrderId, isExpanded)}

              <Box
                sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                onClick={() => toggleGroupExpanded(workOrderId)}
              >
                <Tooltip title={displayNumber} arrow placement="top">
                  <Typography
                    variant="caption"
                    sx={workOrderGroupTitleTextStyles(collapseScheduleItems)}
                    noWrap
                  >
                    {displayNumber}
                  </Typography>
                </Tooltip>
              </Box>

              {renderGroupHeaderActions(workOrderId, items)}
            </Box>

            <Box
              sx={workOrderGroupMetaRowStyles}
              onClick={() => toggleGroupExpanded(workOrderId)}
            >
              <Chip
                size="small"
                label={`${items.length} segment${items.length === 1 ? "" : "s"}`}
                sx={{
                  height: 18,
                  fontSize: "0.58rem",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  color: "primary.main",
                  fontVariantNumeric: "tabular-nums",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {totalGroupHours.toFixed(1)}h total
              </Typography>
              {renderGroupExpandIndicator(isExpanded)}
            </Box>

            {(groupMyReference || groupCustomerName) && (
              <Box
                sx={workOrderGroupDetailRowStyles}
                onClick={() => toggleGroupExpanded(workOrderId)}
              >
                {groupMyReference && (
                  <Tooltip
                    title={`My Reference: ${groupMyReference}`}
                    arrow
                    placement="top"
                  >
                    <Typography
                      variant="caption"
                      sx={workOrderGroupDetailTextStyles}
                    >
                      <Box component="span" sx={workOrderGroupDetailLabelStyles}>
                        My Ref
                      </Box>
                      {groupMyReference}
                    </Typography>
                  </Tooltip>
                )}
                {groupCustomerName && (
                  <Tooltip
                    title={`Customer: ${groupCustomerName}`}
                    arrow
                    placement="top"
                  >
                    <Typography
                      variant="caption"
                      sx={workOrderGroupDetailTextStyles}
                    >
                      <Box component="span" sx={workOrderGroupDetailLabelStyles}>
                        Customer
                      </Box>
                      {groupCustomerName}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Paper>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={workOrderGroupChildrenStyles}>
            {renderDropZone(flatStartIndex)}
            {items.map((item, itemIndexInGroup) => {
              const flatIndex = flatStartIndex + itemIndexInGroup;
              return (
                <Box key={item.id}>
                  {renderScheduledItemPaper(item, flatIndex, {
                    hideWorkOrderNumber: true,
                  })}
                  {renderDropZone(flatIndex + 1, true)}
                </Box>
              );
            })}
          </Box>
        </Collapse>

        {renderDropZone(getFlatDropPosition(unitIndex + 1), true)}
      </Box>
    );
  };

  const statusColor = getCapacityStatusColor(
    availableHours,
    maxCapacity,
    isFull,
  );

  const renderScheduledItemPaper = (
    item: ScheduledItem,
    flatIndex: number,
    options?: { hideWorkOrderNumber?: boolean },
  ) => {
    const hideWorkOrderNumber = options?.hideWorkOrderNumber ?? false;
    const priorityColor = getPriorityColorFromWorkOrder(
      item.workOrderId,
      workOrders,
    );
    const displayNumber =
      item.invoiceNumber || item.workOrderNumber || item.workOrderId;
    const segmentDisplayName = getItemDisplayName(
      item,
      workOrders,
      segmentParts,
    );

    const renderHoursSection = () => (
      <Box
        sx={{
          px: 0.75,
          py: 0.625,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {editingItemId === item.id ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.375,
              minWidth: 0,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.58rem",
                fontWeight: 700,
                color: "text.secondary",
                lineHeight: 1,
                textTransform: "capitalize",
                letterSpacing: "0.04em",
              }}
            >
              Scheduled Hours
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.375,
                minWidth: 0,
              }}
            >
              <TextField
                inputRef={editInputRef}
                size="small"
                type="text"
                value={editDisplayString}
                onChange={(e) => setEditDisplayString(e.target.value)}
                onKeyDown={(e) =>
                  handleEditKeyDown(
                    e as React.KeyboardEvent<HTMLInputElement>,
                    item,
                  )
                }
                onClick={(e) => e.stopPropagation()}
                inputProps={{
                  "aria-label": "Estimated hours",
                  placeholder: "0.00",
                  inputMode: "decimal",
                  style: {
                    textAlign: "right",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    padding: "4px 6px",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: "text.secondary",
                        }}
                      >
                        h
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    pr: 0.5,
                    "&:hover": {
                      bgcolor: "background.paper",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    minWidth: 0,
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 0.25 }}>
                <Tooltip title="Save hours" arrow>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveEditHours(item);
                    }}
                    sx={{
                      p: 0.25,
                      minWidth: 24,
                      minHeight: 24,
                      color: "success.main",
                      bgcolor: (theme) =>
                        alpha(theme.palette.success.main, 0.08),
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.success.main, 0.16),
                      },
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel" arrow>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelEditHours();
                    }}
                    sx={{
                      p: 0.25,
                      minWidth: 24,
                      minHeight: 24,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ) : (
          <Tooltip
            title={
              onUpdateItemHours ? "Click to edit estimated hours" : ""
            }
            arrow
            placement="top"
            disableHoverListener={!onUpdateItemHours}
          >
            <Box
              component="span"
              role={onUpdateItemHours ? "button" : undefined}
              tabIndex={onUpdateItemHours ? 0 : undefined}
              onKeyDown={(e) => {
                if (!onUpdateItemHours) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStartEditHours(item);
                }
              }}
              onClick={(e) => {
                if (!onUpdateItemHours) return;
                e.stopPropagation();
                handleStartEditHours(item);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.375,
                width: "100%",
                fontWeight: 700,
                color: "primary.main",
                bgcolor: "background.paper",
                px: 0.625,
                py: 0.375,
                borderRadius: 1,
                fontSize: "0.72rem",
                lineHeight: 1.4,
                cursor: onUpdateItemHours ? "pointer" : "default",
                border: 1,
                borderColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.2),
                transition: "background-color 0.15s, border-color 0.15s",
                "&:hover": onUpdateItemHours
                  ? {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.08),
                      borderColor: "primary.main",
                    }
                  : undefined,
                "&:focus-visible": onUpdateItemHours
                  ? {
                      outline: 2,
                      outlineColor: "primary.main",
                      outlineOffset: 1,
                    }
                  : undefined,
              }}
            >
              {onUpdateItemHours && (
                <EditIcon
                  sx={{
                    fontSize: 12,
                    opacity: 0.75,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                component="span"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.58rem",
                  fontWeight: 700,
                  textTransform: "capitalize",
                  letterSpacing: "0.04em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Hours
              </Typography>
              <Box
                component="span"
                sx={{
                  ml: "auto",
                  color: "primary.main",
                  fontWeight: 800,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {formatHoursUtil(item.hours)}h
              </Box>
            </Box>
          </Tooltip>
        )}

        {item.technicianOff && (
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
            <Chip
              size="small"
              color="error"
              label="Tech Off"
              sx={{ fontWeight: 600, fontSize: "0.65rem" }}
            />
          </Box>
        )}
      </Box>
    );

    return (
      <Paper
        elevation={draggedItemId === item.id ? 4 : 1}
        data-flat-drop={flatIndex}
        draggable={
          !readOnly &&
          editingItemId !== item.id &&
          savingItemId !== item.id
        }
        onDragStart={(e) => handleItemDragStart(e, item, flatIndex)}
        onDragEnd={clearDragState}
        onDragOver={(e) => handleItemDragOver(e, flatIndex)}
        onDrop={(e) => handleItemDrop(e, flatIndex)}
        sx={{
          p: 0,
          position: "relative",
          overflow: "hidden",
          cursor:
            readOnly
              ? "default"
              : editingItemId === item.id || savingItemId === item.id
                ? "default"
                : "grab",
          "&:active": {
            cursor:
              readOnly
                ? "default"
                : editingItemId === item.id || savingItemId === item.id
                  ? "default"
                  : "grabbing",
          },
          bgcolor:
            draggedItemId === item.id
              ? "primary.light"
              : "background.default",
          border: draggedItemId === item.id ? 2 : 1,
          borderStyle: "solid",
          borderColor:
            draggedItemId === item.id ? "primary.main" : "divider",
          borderRadius: 1,
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          transform:
            draggedItemId === item.id ? "scale(1.05)" : "scale(1)",
          "&:hover": {
            boxShadow: 2,
            transform: "translateY(-1px)",
            ...(scheduleItemView === "active" && {
              borderColor: "primary.main",
            }),
          },
          ...(scheduleItemView === "active"
            ? getStatusBorderStyles(item.woStatusId, priorityColor)
            : getScheduleItemViewBorderStyles(theme, scheduleItemView)),
        }}
      >
        {!hideWorkOrderNumber ? (
          <Box sx={workOrderGroupTitleRowStyles}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={displayNumber} arrow placement="top">
                <Typography
                  variant="caption"
                  sx={workOrderGroupTitleTextStyles(collapseScheduleItems)}
                  noWrap
                >
                  {displayNumber}
                </Typography>
              </Tooltip>
            </Box>
            {renderScheduleItemActions(item)}
          </Box>
        ) : (
          <Box sx={scheduleItemActionHeaderStyles}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip
                title={segmentDisplayName}
                arrow
                placement="top"
                disableHoverListener={!segmentDisplayName}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                  noWrap
                >
                  {segmentDisplayName}
                </Typography>
              </Tooltip>
            </Box>
            {renderScheduleItemActions(item)}
          </Box>
        )}

        {collapseScheduleItems ? (
          !hideWorkOrderNumber ? (
            <Box sx={{ px: 0.75, py: 0.5, minWidth: 0 }}>
              <Tooltip
                title={segmentDisplayName}
                arrow
                placement="top"
                disableHoverListener={!segmentDisplayName}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    minWidth: 0,
                    fontSize: "0.62rem",
                    color: "text.secondary",
                    lineHeight: 1.15,
                  }}
                  noWrap
                >
                  {segmentDisplayName}
                </Typography>
              </Tooltip>
            </Box>
          ) : null
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Box sx={{ minWidth: 0, px: 0.75, py: 0.625 }}>
              {!hideWorkOrderNumber && (
                <Tooltip
                  title={segmentDisplayName}
                  arrow
                  placement="top"
                  disableHoverListener={!segmentDisplayName}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: 0.35,
                      fontSize: "0.72rem",
                      lineHeight: 1.25,
                      ...scheduleItemReadableTextSx,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {segmentDisplayName}
                  </Typography>
                </Tooltip>
              )}

              {item.customerName && (
                <Tooltip title={item.customerName} arrow placement="top">
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.65rem",
                      fontWeight: 500,
                      mb: 0.25,
                      ...scheduleItemReadableTextSx,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.customerName}
                  </Typography>
                </Tooltip>
              )}

              {item.woDescription && (
                <Tooltip title={item.woDescription} arrow placement="top">
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.6rem",
                      mb: 0.25,
                      ...scheduleItemReadableTextSx,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.woDescription}
                  </Typography>
                </Tooltip>
              )}

              {item.unitModel && (
                <Tooltip title={item.unitModel} arrow placement="top">
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.6rem",
                      ...scheduleItemReadableTextSx,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      Unit:
                    </Box>{" "}
                    {item.unitModel}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            {renderHoursSection()}
          </Box>
        )}

        {savingItemId === item.id && (
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 3,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              "& .MuiLinearProgress-bar": {
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
              },
            }}
          />
        )}
      </Paper>
    );
  };

  return (
    <>
    <Card
      elevation={dragOver ? 8 : 2}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 200,
        height: "100%",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        border: dragOver ? 3 : isDisabled ? 2 : isScheduledInPast ? 2 : 1,
        borderStyle: "solid",
        borderColor: isDisabled
          ? "error.main"
          : dragOver
            ? "primary.main"
            : isScheduledInPast
              ? "warning.main"
              : "divider",
        bgcolor: isDisabled
          ? "error.light"
          : dragOver
            ? "primary.light"
            : isScheduledInPast
              ? alpha(theme.palette.warning.light, 0.08)
              : "background.paper",
        borderRadius: 2,
        position: "relative",
        opacity: isDisabled ? 0.7 : 1,
        cursor: isDisabled ? "not-allowed" : "default",
        "&::before":
          dragOver && !isDisabled
            ? {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                bgcolor: "primary.main",
                zIndex: 1,
              }
            : isDisabled
              ? {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  bgcolor: "error.main",
                  zIndex: 1,
                }
              : isScheduledInPast
                ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    bgcolor: "warning.main",
                    zIndex: 1,
                  }
                : {},
        transform: dragOver && !isDisabled ? "scale(1.02)" : "scale(1)",
        boxShadow: isDisabled
          ? "0 2px 8px rgba(211, 47, 47, 0.2)"
          : dragOver
            ? "0 8px 24px rgba(25, 118, 210, 0.3)"
            : isScheduledInPast
              ? `0 0 12px 2px ${alpha(theme.palette.warning.main, 0.35)}`
              : "0 2px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Header - Capacity info */}
      <Box
        sx={{
          bgcolor: isScheduledInPast
            ? alpha(theme.palette.warning.light, 0.12)
            : "background.paper",
          borderBottom: 2,
          borderColor: isScheduledInPast ? "warning.main" : "divider",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Capacity info row */}
        <Box
          sx={{
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 0.75, sm: 1, md: 1.25 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "nowrap",
            bgcolor: isDisabled
              ? theme.palette.mode === "dark"
                ? "rgba(211, 47, 47, 0.25)"
                : "error.light"
              : dragOver
                ? theme.palette.mode === "dark"
                  ? "rgba(25, 118, 210, 0.25)"
                  : "action.hover"
                : isScheduledInPast
                  ? alpha(theme.palette.warning.light, 0.12)
                  : "background.paper",
            color: "text.primary",
            transition: "all 0.2s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              color: "text.primary",
              flexWrap: "nowrap",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 14 }, flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {totalHours.toFixed(1)}h / {maxCapacity.toFixed(1)}h
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              flexWrap: "nowrap",
              flexShrink: 0,
            }}
          >
            <Tooltip title="Add Work Order" arrow placement="top">
              <IconButton
                size="small"
                onClick={() => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const dayOfMonth = String(date.getDate()).padStart(2, "0");
                  onOpenSmartWo?.(
                    technician.identity,
                    `${year}-${month}-${dayOfMonth}`,
                  );
                }}
                sx={{ p: { xs: 0.25, sm: 0.5 } }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: { xs: "18px", sm: "20px", md: "22px" } }} />
              </IconButton>
            </Tooltip>
            {/* Color indicator circle */}
            <Box
              sx={getCapacityIndicatorStyles(
                availableHours,
                maxCapacity,
                isFull,
              )}
            />
            {(isTechnicianNotWorking || isLocationClosed) && (
              <Tooltip
                arrow
                placement="top"
                title={
                  isLocationClosed && isTechnicianNotWorking
                    ? "Location closed and technician is off"
                    : isLocationClosed
                      ? "Location is closed"
                      : "Technician is off"
                }
              >
                <Chip
                  size="small"
                  color="error"
                  label="Off"
                  sx={{ fontWeight: 600, fontSize: "0.6rem", height: 18 }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Capacity bar */}
        <Box sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pb: { xs: 0.75, sm: 1, md: 1.25 }, pt: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={
              maxCapacity > 0
                ? Math.min((totalHours / maxCapacity) * 100, 100)
                : 100
            }
            color={statusColor}
            sx={{
              height: 8,
              borderRadius: 2,
              bgcolor: "action.hover",
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </Box>

      {/* Scheduled items */}
      <Stack
        sx={{
          flex: 1,
          p: 1.25,
          gap: 1,
          bgcolor: isDisabled ? "error.light" : "background.default",
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "action.hover",
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "primary.light",
            borderRadius: 3,
            "&:hover": {
              bgcolor: "primary.main",
            },
          },
        }}
        onDragOver={(e) => {
          if (!canDrop && !activeDragRef.current) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }}
        onDrop={(e) => {
          e.stopPropagation();
          handleDrop(e);
        }}
      >
        {statusMessage && (
          <Box
            sx={{
              textAlign: "center",
              py: 2,
              px: 1,
              bgcolor:
                isLocationClosed || isTechnicianNotWorking
                  ? "error.main"
                  : "warning.main",
              color: "white",
              borderRadius: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: "0.75rem", color: "white" }}
            >
              {statusMessage}
            </Typography>
          </Box>
        )}
        {scheduledItems.length === 0 && !isDisabled ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: "text.secondary",
              py: 3,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 500, fontSize: "0.75rem" }}
              >
                Drop segments here
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {renderDropZone(0)}
            {renderUnits.map((unit, unitIndex) =>
              renderWorkOrderGroup(unit, unitIndex),
            )}
          </>
        )}
      </Stack>
    </Card>
    </>
  );
}

// Memoize to prevent unnecessary re-renders
// Only re-render when props actually change
export const TechnicianCard = memo(
  TechnicianCardComponent,
  (prevProps, nextProps) => {
    // Custom comparison function for better performance
    return (
      prevProps.technician.identity === nextProps.technician.identity &&
      prevProps.day === nextProps.day &&
      prevProps.date?.getTime() === nextProps.date?.getTime() &&
      prevProps.availableHours === nextProps.availableHours &&
      prevProps.baseCapacity === nextProps.baseCapacity &&
      prevProps.isLocationClosed === nextProps.isLocationClosed &&
      prevProps.isTechnicianNotWorking === nextProps.isTechnicianNotWorking &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.scheduleItemView === nextProps.scheduleItemView &&
      prevProps.scheduledItems.length === nextProps.scheduledItems.length &&
      // Deep compare scheduledItems by ID and sortKey (only check if changed)
      prevProps.scheduledItems.every((item, idx) => {
        const nextItem = nextProps.scheduledItems[idx];
        return (
          nextItem &&
          item.id === nextItem.id &&
          item.sortKey === nextItem.sortKey &&
          item.hours === nextItem.hours
        );
      })
    );
  },
);
